import React from "react";
import { Responsive, WidthProvider } from "react-grid-layout";
import { LoadingSpinner } from "src/components/application/LoadingSpinner";
import Layout from "src/components/core/Layout";
import {
  ChartName,
  ChartTypesMap,
  DefaultDate,
} from "src/components/Referral-partner/Analytics/analyticsTypes";
import useGetChartConfig from "src/components/Referral-partner/Analytics/configs/useGetChartConfig";
import DateSelection from "src/components/Referral-partner/Analytics/DateSelection";
import GlobalFilter from "src/components/Referral-partner/Analytics/GlobalFilter";
import {
  AnalyticsSubtitle,
  AnalyticsText,
  AnalyticsTitleWrapper,
  ChartHeader,
  DraggableWrapper,
  FilterWrapper,
  GridContainer,
  GridSection,
  HeadingText,
  LoadingGridSection,
  MainHeaderWrapper,
  MainMenuWrapper,
  MenuWrapper,
  PageWrapper,
} from "src/components/Referral-partner/Analytics/styles/analytics.styles";
import { useActiveCharts } from "src/components/Referral-partner/Analytics/useActiveCharts";
import { useChartLayouts } from "src/components/Referral-partner/Analytics/useChartLayouts";
import InfoIcon from "src/icons/portfolio/InfoIcon";
import api from "src/store/features/api";
import {
  changeChartDate,
  changeGlobalDate,
  useRPAnalyticsState,
} from "src/store/features/RPAnalytics.slice";
import { useAppDispatch } from "src/store/store";
import "/node_modules/react-grid-layout/css/styles.css";
import "/node_modules/react-resizable/css/styles.css";

const ResponsiveGridLayout = WidthProvider(Responsive);

const RPAnalytics = () => {
  // Theme toggle state
  const [useDefaultTheme, toggle] = React.useReducer((theme) => !theme, true);

  // API Integration
  const { data: rpCode } = api.useGetReferralCodeQuery();
  const dispatch = useAppDispatch();
  const { chartDateRange } = useRPAnalyticsState();

  // Chart configuration and state
  const chartConfig = useGetChartConfig();

  // Layout management
  const { DEFAULT_ACTIVE_CHARTS, activeCharts, addChart, removeChart } = useActiveCharts();
  const { getSavedLayouts, onLayoutChange } = useChartLayouts({ activeCharts });

  /**
   * Updates the global date range filter
   * @param newDate - The new date range to set
   */
  const handleGlobalDateClick = (newDate: DefaultDate): void => {
    dispatch(changeGlobalDate(newDate));
  };

  /**
   * Updates the date range for a specific chart
   * @param chartName - The chart to update
   * @param newDate - The new date range to set
   */
  const handleDateClick = (chartName: ChartName, newDate: DefaultDate): void => {
    dispatch(changeChartDate({ [chartName]: newDate } as Record<ChartName, DefaultDate>));
  };

  /**
   * Filters out active charts to get available charts for the selector
   * @param chartTypes - All available chart types
   * @param activeCharts - Currently displayed charts
   * @returns Map of available charts
   */
  const getAvailableCharts = (
    chartTypes: ChartTypesMap,
    activeCharts: ChartName[],
  ): ChartTypesMap => {
    return Object.entries(chartTypes).reduce((acc, [type, chart]) => {
      if (!activeCharts.includes(type as ChartName)) {
        acc[type as ChartName] = chart;
      }
      return acc;
    }, {} as ChartTypesMap);
  };

  return (
    <Layout toggleTheme={toggle} useDefaultTheme={useDefaultTheme}>
      <PageWrapper>
        {/* Title and referral code */}
        <AnalyticsTitleWrapper>
          <AnalyticsText>
            <AnalyticsSubtitle>Your analytics</AnalyticsSubtitle> -{" "}
            <div>{rpCode?.referralCode}</div>
          </AnalyticsText>

          {/* Filter - date and add charts */}
          <FilterWrapper>
            <GlobalFilter
              globalDate={"all time"}
              showChartSelector={activeCharts.length < DEFAULT_ACTIVE_CHARTS.length}
              onDateClick={handleGlobalDateClick}
              onChartClick={addChart}
              charts={getAvailableCharts(chartConfig, activeCharts)}
            />
          </FilterWrapper>
        </AnalyticsTitleWrapper>

        {/* Grid layout */}
        <GridContainer>
          <ResponsiveGridLayout
            className="layout"
            layouts={getSavedLayouts()}
            // 12 cols works well because it's divisible by both 2 and 4
            cols={{ lg: 12, md: 12, sm: 6, xs: 4, xxs: 2 }}
            breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
            containerPadding={[8, 8]}
            onLayoutChange={onLayoutChange}
            margin={[36, 36]}
            isDraggable={true}
            draggableHandle=".drag-handle"
            isResizable={false}
            useCSSTransforms>
            {activeCharts.map((chartName: ChartName): JSX.Element => {
              const chart = chartConfig[chartName];

              const {
                data,
                title,
                menuMode,
                dateOptions,
                tooltip,
                mainChart = false,
                isLoading,
                styles = {},
              } = chart;
              const date = chartDateRange[chartName];

              if (isLoading) {
                return (
                  <LoadingGridSection key={chartName} className="chart-container">
                    <LoadingSpinner size={40} />
                  </LoadingGridSection>
                );
              }

              return (
                <GridSection key={chartName} style={styles} className="chart-container">
                  {mainChart ? (
                    <MainHeaderWrapper>
                      <MainMenuWrapper className="remove-button">
                        <DateSelection
                          mode={menuMode}
                          chart={chart}
                          chartName={chartName}
                          date={date}
                          dateOptions={dateOptions}
                          onClick={handleDateClick}
                          onRemoveChart={(): void => removeChart(chartName)}
                        />
                      </MainMenuWrapper>
                    </MainHeaderWrapper>
                  ) : (
                    <ChartHeader id={`${chartName}-header`}>
                      <DraggableWrapper className="drag-handle">
                        <HeadingText>
                          {title} {tooltip && <InfoIcon tooltip={tooltip} color="#002035" />}
                        </HeadingText>
                      </DraggableWrapper>
                      <MenuWrapper className="remove-button">
                        <DateSelection
                          mode={menuMode}
                          chart={chart}
                          chartName={chartName}
                          date={date}
                          dateOptions={dateOptions}
                          onClick={handleDateClick}
                          onRemoveChart={(): void => removeChart(chartName)}
                        />
                      </MenuWrapper>
                    </ChartHeader>
                  )}
                  <chart.component title={title} data={data as any} />
                </GridSection>
              );
            })}
          </ResponsiveGridLayout>
        </GridContainer>
      </PageWrapper>
    </Layout>
  );
};

export default RPAnalytics;

// new colour scheme:
// slategrey: #d4e3db
// yellow: #ffca59
// orange: #ffae58
// softgreen: #bce0a4
// softblue: #a0cfdd
// softpurple: #a1a1ea
// softred: #e66d79
// coral: #fe9467
// hardblue: #61c0eb
